import { NODE_TYPES } from '../../constants';
import { AnyNode, FieldMatchNodeData, Node, StartNodeData } from '../../types';
import fieldsMatchUtils from './fieldsMatchUtils';
import { getNodeTypeFromNode } from './helpers';

interface ValidateNodeOptions {
  /** The start node */
  startNode?: Node;
}

/**
 * Check whether or not a node has an error
 * @param node The node to check
 * @param param1 Optional arguments
 * @returns Whether or not the node has an error
 */
const doesNodeHaveError = (node: AnyNode, { startNode }: ValidateNodeOptions = {}) => {
  return (
    node.type === NODE_TYPES.FIELDS_MATCH &&
    !fieldsMatchUtils.isValidForTrigger(
      node.nodeData as FieldMatchNodeData,
      startNode?.nodeData as StartNodeData,
    )
  );
};

/**
 * Validates the node and returns a new node with the `hasError` field set
 * @param {Node} node The node to validate
 * @param {ValidateNodeOptions} options Optional arguments
 * @returns The validated node
 */
export const validateNode = (node: AnyNode, { startNode }: ValidateNodeOptions = {}) => {
  if (node.type !== NODE_TYPES.FIELDS_MATCH) return node;
  return { ...node, hasError: doesNodeHaveError(node, { startNode }) };
};

/**
 * Check which nodes have errors and return a list of their ids
 * @param nodes The nodes to check
 * @returns List of ids of nodes with errors
 */
export const getNodesWithErrors = (nodes: Node[] = []) => {
  const startNode = nodes.find((n) => getNodeTypeFromNode(n) === NODE_TYPES.START);
  return nodes.reduce((acc: string[], node) => {
    if (doesNodeHaveError(node, { startNode })) {
      return [...acc, node.id];
    }
    return acc;
  }, []);
};
